import { useEffect, useState, type ReactElement } from 'react';
import { kopiere } from '../kopieren';
import { api, type DiagnosisView, type FailureView, type MesswertZeile } from '../api';
import { Sheet } from './Sheet';

/**
 * Why something did not work, without a shell on the server: is a worker
 * alive, what waits, what hangs, and every failure with ffmpeg's own words.
 * "Bericht kopieren" puts the whole thing on the clipboard as text, which is
 * the fastest way to hand it to whoever is fixing it.
 */

function ago(seconds: number): string {
  if (seconds < 90) return `vor ${seconds} s`;
  if (seconds < 5400) return `vor ${Math.round(seconds / 60)} min`;
  return `vor ${(seconds / 3600).toFixed(1)} h`;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

const KIND: Record<FailureView['kind'], string> = { ingest: 'Aufbereitung', render: 'Export', transcribe: 'Transkription' };

function Failure({ failure }: { failure: FailureView }): ReactElement {
  const [open, setOpen] = useState(false);
  const probe = failure.probe;
  return (
    <div className="row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }} data-failure>
      <div className="spread">
        <div className="grow">
          <div className="title">{failure.name}</div>
          <div className="sub">
            {KIND[failure.kind]} · {when(failure.created_at)}
            {failure.project_name ? ` · ${failure.project_name}` : ''}
          </div>
        </div>
        <span className="badge err">Fehler</span>
      </div>
      <div className="sub" style={{ color: 'var(--err-text)', whiteSpace: 'pre-wrap' }}>
        {failure.message.split('\n')[0]}
      </div>
      {probe ? (
        <div className="sub" style={{ whiteSpace: 'pre-wrap' }}>
          {probe.error
            ? `ffprobe: ${probe.error}`
            : `${probe.format.split(',')[0]} · ${probe.durationSec.toFixed(1)} s · ${(probe.sizeBytes / 1024 / 1024).toFixed(1)} MB`}
          {probe.streams.map((s) => (
            <div key={s.index}>
              #{s.index} {s.type} {s.codec}
              {s.profile ? ` ${s.profile}` : ''}
              {s.pixFmt ? ` ${s.pixFmt}` : ''}
              {s.width ? ` ${s.width}×${s.height}` : ''}
              {s.colour ? ` ${s.colour}` : ''}
              {s.rotation ? ` gedreht ${s.rotation}°` : ''}
              {s.sideData?.length ? ` [${s.sideData.join(', ')}]` : ''}
            </div>
          ))}
        </div>
      ) : null}
      <button className="small ghost" style={{ alignSelf: 'flex-start', padding: '4px 0' }} onClick={() => setOpen(!open)}>
        {open ? 'Weniger' : 'ffmpeg-Ausgabe und Befehl'}
      </button>
      {open ? (
        <div className="sub" style={{ whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace', fontSize: 11, userSelect: 'text' }}>
          {failure.args?.length ? `${failure.args.join(' ')}\n\n` : ''}
          {failure.stderr || failure.message}
          {failure.ffmpeg ? `\n\n${failure.ffmpeg}` : ''}
        </div>
      ) : null}
    </div>
  );
}

/** Kurz, was für ein Gerät das war -- die ganze Browser-Kennung liest niemand. */
function geraet(ua: string): string {
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Gerät';
}

/**
 * Ein Befund in einer Zeile: wie flüssig es lief, und was es aufgehalten hat.
 *
 * „lief 1,0-fach“ heisst Echtzeit; darunter ist es zäh. Die beiden Zahlen
 * daneben trennen die zwei Ursachen, die sich gleich anfühlen: fehlende Daten
 * und ein Dekoder, der nicht mitkommt (7.69q/r).
 */
function Befund({ zeile }: { zeile: MesswertZeile }): ReactElement {
  const d = zeile.daten ?? {};
  const p = d.puffer ?? {};
  const b = d.aufsBild ?? {};
  const tempo = typeof d.kopfProSekunde === 'number' ? d.kopfProSekunde : null;
  const zaeh = tempo !== null && tempo < 0.9;
  return (
    <div className="row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
      <div className="spread">
        <div className="title" style={{ fontSize: 13 }}>
          {geraet(zeile.ua)} · {new Date(zeile.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
        </div>
        <span className={`badge ${zaeh ? 'err' : 'ok'}`}>{tempo !== null ? `${tempo.toFixed(2)}-fach` : d.modus ?? '—'}</span>
      </div>
      <div className="sub">
        {p.haltungen
          ? `${p.haltungen}× angehalten (${Math.round((p.msGesamt ?? 0) / 100) / 10} s, längste ${Math.round((p.msMax ?? 0) / 100) / 10} s${p.abgelaufen ? `, ${p.abgelaufen}× abgelaufen` : ''})`
          : 'nie angehalten'}
        {' · '}
        {b.haltungen ? `${b.haltungen}× auf ein Bild gewartet (${Math.round((b.msGesamt ?? 0) / 100) / 10} s)` : 'nie auf ein Bild gewartet'}
        {d.modus ? ` · ${d.modus}` : ''}
        {d.takt ? ` · Takt ${d.takt}` : ''}
        {/* Der Anlauf steht neben dem Tempo und nicht mehr darin (7.69v):
            „langsam losgekommen“ und „langsam gelaufen“ sind zwei
            verschiedene Fehler mit zwei verschiedenen Ursachen. */}
        {typeof d.anlaufMs === 'number' ? ` · ${(d.anlaufMs / 1000).toFixed(2)} s bis zur ersten Bewegung` : ''}
        {d.anlaufMs === null ? ' · nichts bewegt' : ''}
        {d.ram ? ` · ${d.ram.dateien} Datei(en) im Speicher (${Math.round((d.ram.bytes ?? 0) / 1024 / 102.4) / 10} MB)` : ''}
      </div>
    </div>
  );
}

export function DiagnoseSheet({ onClose }: { onClose: () => void }): ReactElement {
  const [d, setD] = useState<DiagnosisView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [key, setKey] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [befunde, setBefunde] = useState<MesswertZeile[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setD((await api.diagnose()).diagnosis);
      // Die Gerätebefunde daneben: sie erklären ein Stottern, das auf dem
      // Server nirgends steht (7.69r).
      void api
        .geraetebefunde(12)
        .then((r) => setBefunde(r.messwerte))
        .catch(() => setBefunde([]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Diagnose konnte nicht geladen werden.');
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const copy = async () => {
    try {
      const text = await api.diagnoseText();
      if (!(await kopiere(text))) throw new Error('nicht kopiert');
      setCopied('Bericht kopiert — einfach in den Chat einfügen.');
    } catch {
      setCopied('Kopieren ging nicht; der Bericht steht unter /api/diagnose?format=text.');
    }
  };

  const aufraeumen = async () => {
    setBusy(true);
    try {
      const { entfernt, geblieben } = await api.altlastenAufraeumen();
      setNote(
        entfernt
          ? `${entfernt} erledigte${entfernt === 1 ? 'r Fehler' : ' Fehler'} weggeräumt${geblieben ? `, ${geblieben} steh${geblieben === 1 ? 't' : 'en'} noch offen` : ''}.`
          : geblieben
            ? `Nichts wegzuräumen — alle ${geblieben} Fehler betreffen etwas, das noch nicht in Ordnung ist.`
            : 'Da ist nichts.',
      );
      await load();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Aufräumen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const requeue = async () => {
    setBusy(true);
    try {
      const { report } = await api.requeueStuck();
      const n = report.assets.length + report.transcripts.length;
      setNote(
        n
          ? `${n} Auftrag${n === 1 ? '' : 'äge'} neu eingereiht: ${[...report.assets.map((a) => a.name), ...report.transcripts.map(() => 'Transkript')].join(', ')}.`
          : 'Nichts nachzuholen.',
      );
      await load();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Nachholen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  /** Ob der Schlüssel wirklich in der Zwischenablage gelandet ist. */
  const [keyKopiert, setKeyKopiert] = useState<boolean | null>(null);

  const makeKey = async () => {
    setBusy(true);
    try {
      const res = await api.createDiagnoseToken();
      setKey(res.token);
      // Hier wird *nicht* kopiert: die Beruehrung, die den Knopf gedrueckt
      // hat, ist nach der Anfrage vorbei, und Safari lehnt einen
      // Schreibversuch danach ab. Das stand trotzdem als „Kopiert“ da --
      // deshalb steht der Schluessel jetzt sichtbar da, mit einem eigenen
      // Knopf daneben (Sascha, 7. September: „ich kann ihn leider nicht
      // kopieren“).
      setKeyKopiert(null);
      await load();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Schlüssel konnte nicht erzeugt werden.');
    } finally {
      setBusy(false);
    }
  };

  const revokeKey = async () => {
    setBusy(true);
    try {
      await api.revokeDiagnoseToken();
      setKey(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const workerOk = d ? d.workers.some((w) => w.ageSeconds < 120) : null;
  const queueTotal = (key: 'waiting' | 'active' | 'failed' | 'failedOffen') =>
    d ? Object.values(d.queues).reduce((sum, q) => sum + (q.counts[key] ?? 0), 0) : 0;
  // Erledigt heißt: der Fehlschlag betrifft etwas, das heute in Ordnung ist.
  const altlasten = queueTotal('failed') - queueTotal('failedOffen');

  return (
    <Sheet title="Diagnose" onClose={onClose}>
      {error ? <p className="error-text">{error}</p> : null}
      {!d && !error ? <p className="hint">Wird gesammelt …</p> : null}
      {d ? (
        <>
          <div className="card" style={{ margin: '0 0 14px' }} data-worker={workerOk ? 'ok' : 'missing'}>
            <div className="spread">
              <div>
                <div className="title">{workerOk ? 'Worker läuft' : 'Kein Worker meldet sich'}</div>
                <div className="sub">
                  {d.workers.length
                    ? d.workers.map((w) => `${w.host} · Herzschlag ${ago(w.ageSeconds)}`).join(' · ')
                    : 'Kein Worker. In Portainer den Container worker prüfen.'}
                </div>
              </div>
              <span className={`badge ${workerOk ? 'ok' : 'err'}`}>{workerOk ? 'da' : 'fehlt'}</span>
            </div>
            <div className="sub" style={{ marginTop: 8 }}>
              {/* Was zählt, ist das Offene: ein Fehlschlag, dessen Medium
                  längst bereitliegt, ist Vergangenheit und keine Meldung. */}
              Warteschlangen: {queueTotal('waiting')} wartend · {queueTotal('active')} aktiv ·{' '}
              {queueTotal('failedOffen')} fehlgeschlagen
              {altlasten > 0 ? ` (${altlasten} erledigt)` : ''}
              {d.version.commit ? ` · Stand ${d.version.commit.slice(0, 7)}` : ''}
            </div>
          </div>

          {d.stuck.length ? (
            <>
              <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Hängt</h3>
              <div className="list">
                {d.stuck.map((s) => (
                  <div className="row" key={s.id}>
                    <div className="grow">
                      <div className="title">{s.name}</div>
                      <div className="sub">
                        {s.status} seit {s.minutes} min
                      </div>
                    </div>
                    <span className="badge busy">hängt</span>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>
            Fehler{d.failures.length ? ` (${d.failures.length})` : ''}
          </h3>
          {d.failures.length ? (
            <div className="list">
              {d.failures.map((f) => (
                <Failure key={f.id} failure={f} />
              ))}
            </div>
          ) : (
            <p className="hint" style={{ marginTop: 0 }}>Kein Fehler aufgezeichnet.</p>
          )}

          <button className="primary" style={{ width: '100%', marginTop: 16 }} onClick={() => void copy()}>
            Bericht kopieren
          </button>
          {copied ? <p className="hint">{copied}</p> : null}

          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Nachholen</h3>
          <p className="hint" style={{ marginTop: 0 }}>
            Reiht wieder ein, was seit zehn Minuten hängt. Läuft auch von selbst.
          </p>
          <button style={{ width: '100%' }} onClick={() => void requeue()} disabled={busy} data-requeue>
            Hängende Aufträge nachholen
          </button>
          {altlasten > 0 ? (
            <>
              <p className="hint" style={{ marginTop: 12 }}>
                {altlasten === 1 ? 'Ein Fehlschlag betrifft' : `${altlasten} Fehlschläge betreffen`} etwas, das
                inzwischen in Ordnung ist — oder ist so alt, dass er nichts mehr über heute sagt. Wegräumen ändert
                nichts an den Medien, nur an dieser Liste.
              </p>
              <button style={{ width: '100%' }} onClick={() => void aufraeumen()} disabled={busy} data-aufraeumen>
                Erledigte Fehler wegräumen
              </button>
            </>
          ) : null}
          {note ? <p className="hint" data-note>{note}</p> : null}

          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Vom Gerät</h3>
          <p className="hint" style={{ marginTop: 0 }}>
            Was die Schnittplätze beim Abspielen von selbst geschickt haben — neueste zuerst. „Angehalten“ heißt: die
            Wiedergabe hat auf Daten gewartet. „Auf ein Bild“ heißt: die Daten waren da, der Dekoder noch nicht.
          </p>
          {befunde === null ? <p className="hint">Wird geholt …</p> : null}
          {befunde?.length === 0 ? <p className="hint">Noch keiner. Er entsteht beim Abspielen von selbst.</p> : null}
          {befunde?.length ? (
            <>
              <div className="list" data-befunde>
                {befunde.map((b) => (
                  <Befund key={b.id} zeile={b} />
                ))}
              </div>
              <button
                style={{ width: '100%', marginTop: 10 }}
                onClick={() => void kopiere(JSON.stringify(befunde, null, 2)).then(() => setCopied('Gerätebefunde kopiert.'))}
              >
                Gerätebefunde kopieren
              </button>
            </>
          ) : null}

          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Fernwartung</h3>
          <p className="hint" style={{ marginTop: 0 }}>
            Liest die Diagnose ohne Anmeldung und holt hängende Aufträge nach, sonst nichts. Wird einmal angezeigt.
            {d.remoteKey.stored
              ? ` Es gibt einen, erzeugt ${new Date(d.remoteKey.stored).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}.`
              : d.remoteKey.fromEnv
                ? ' Einer ist über die Umgebung gesetzt.'
                : ' Bisher gibt es keinen.'}
          </p>
          {key ? (
            <div className="card" style={{ margin: '0 0 10px' }} data-key>
              {/* Auswählbar, und zwar auch auf dem iPhone: der `body` sperrt
                  Auswahl und Halte-Menü für die ganze App (damit ein Zug an
                  einem Clip kein Wort markiert). Beides muss hier ausdrücklich
                  zurückgenommen werden, sonst geht nicht einmal antippen und
                  halten. */}
              <div
                className="sub"
                data-key-text
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  wordBreak: 'break-all',
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  WebkitTouchCallout: 'default',
                }}
              >
                {key}
              </div>
              <button
                className="chip"
                style={{ marginTop: 8 }}
                data-key-kopieren
                onClick={() => void kopiere(key).then(setKeyKopiert)}
              >
                Schlüssel kopieren
              </button>
              <div className="hint" style={{ marginTop: 6 }}>
                {keyKopiert === true
                  ? 'Kopiert.'
                  : keyKopiert === false
                    ? 'Kopieren ging nicht — antippen und halten, dann „Kopieren“.'
                    : 'Wird nur jetzt angezeigt.'}
              </div>
            </div>
          ) : null}
          <div className="chips">
            <button className="chip" onClick={() => void makeKey()} disabled={busy}>
              {d.remoteKey.stored ? 'Neuen Schlüssel erzeugen' : 'Schlüssel erzeugen'}
            </button>
            {d.remoteKey.stored ? (
              <button className="chip" onClick={() => void revokeKey()} disabled={busy}>
                Widerrufen
              </button>
            ) : null}
          </div>
          <p className="hint">
            Enthält Worker, Warteschlangen, hängende Aufträge, Fehler und Speicher. Keine Zugangsdaten.
          </p>
        </>
      ) : null}
    </Sheet>
  );
}
